import { useSelector, useDispatch } from 'react-redux';
import { useCallback } from 'react';
import { RootState, AppDispatch } from '../stores/store';
import {
  fetchTours,
  fetchDiscords,
  fetchRulesets,
  createTour as createTourAction,
  updateTour as updateTourAction,
  deleteTour as deleteTourAction,
  fetchTourById,
  setEditingTour,
  clearEditingTour,
  setFilters,
  clearFilters,
  clearErrors,
  clearError,
} from '../features/tours/tourSlice';
import { Tour, TourFilters, filterTours } from '../models/tour';

// Main hook for tour management
export const useTours = () => {
  const dispatch = useDispatch<AppDispatch>();
  const tourState = useSelector((state: RootState) => state.tours);

  // Memoized selectors
  const filteredTours = useCallback(() => {
    return filterTours(tourState.tours, tourState.filters);
  }, [tourState.tours, tourState.filters]);

  // Actions
  const actions = {
    // Fetch actions
    fetchTours: useCallback(() => dispatch(fetchTours()), [dispatch]),
    fetchDiscords: useCallback(() => dispatch(fetchDiscords()), [dispatch]),
    fetchRulesets: useCallback(() => dispatch(fetchRulesets()), [dispatch]),
    fetchTourById: useCallback((id: string) => dispatch(fetchTourById(id)), [dispatch]),

    // CRUD actions
    createTour: useCallback((tour: Tour) => dispatch(createTourAction(tour)), [dispatch]),
    updateTour: useCallback(
      (id: string, tour: Tour) => dispatch(updateTourAction({ id, tour })),
      [dispatch]
    ),
    deleteTour: useCallback((id: string) => dispatch(deleteTourAction(id)), [dispatch]),

    // UI state actions
    setEditingTour: useCallback((tour: Tour | null) => dispatch(setEditingTour(tour)), [dispatch]),
    clearEditingTour: useCallback(() => dispatch(clearEditingTour()), [dispatch]),
    setFilters: useCallback((filters: TourFilters) => dispatch(setFilters(filters)), [dispatch]),
    clearFilters: useCallback(() => dispatch(clearFilters()), [dispatch]),
    clearErrors: useCallback(() => dispatch(clearErrors()), [dispatch]),
    clearError: useCallback(
      (errorType: keyof typeof tourState.error) => dispatch(clearError(errorType)),
      [dispatch, tourState]
    ),
  };

  return {
    // State
    tours: tourState.tours,
    discords: tourState.discords,
    rulesets: tourState.rulesets,
    loading: tourState.loading,
    error: tourState.error,
    filters: tourState.filters,
    editingTour: tourState.editingTour,

    // Computed
    filteredTours: filteredTours(),

    // Actions
    ...actions,
  };
};

// Hook for initializing tour data
export const useInitializeTours = () => {
  const { fetchTours, fetchDiscords, fetchRulesets, loading, error } = useTours();

  const initializeData = useCallback(async () => {
    try {
      await Promise.all([fetchTours(), fetchDiscords(), fetchRulesets()]);
    } catch (error) {
      console.error('Failed to initialize tour data:', error);
    }
  }, [fetchTours, fetchDiscords, fetchRulesets]);

  const isLoading = loading.tours || loading.discords || loading.rulesets;
  const hasError = error.tours || error.discords || error.rulesets;

  return {
    initializeData,
    isLoading,
    hasError,
  };
};

// Hook for tour form management
export const useTourForm = () => {
  const {
    editingTour,
    loading,
    error,
    createTour,
    updateTour,
    setEditingTour,
    clearEditingTour,
    clearError,
  } = useTours();

  const handleSubmit = useCallback(
    async (tour: Tour) => {
      if (editingTour && editingTour.id) {
        return updateTour(editingTour.id, { ...tour, id: editingTour.id });
      } else {
        return createTour(tour);
      }
    },
    [editingTour, createTour, updateTour]
  );

  const handleEdit = useCallback(
    (tour: Tour) => {
      setEditingTour(tour);
      // Scroll to form
      setTimeout(() => {
        document.getElementById('tour-form-section')?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    },
    [setEditingTour]
  );

  const handleCancel = useCallback(() => {
    clearEditingTour();
  }, [clearEditingTour]);

  const isLoading = loading.creating || loading.updating;
  const formError = error.creating || error.updating;

  return {
    editingTour,
    isLoading,
    error: formError,
    handleSubmit,
    handleEdit,
    handleCancel,
    clearError,
  };
};

// Hook for tour table management
export const useTourTable = () => {
  const { filteredTours, discords, rulesets, loading, error, deleteTour, clearError } = useTours();

  const { handleEdit } = useTourForm();

  const handleDelete = useCallback(
    (id: string) => {
      return deleteTour(id);
    },
    [deleteTour]
  );

  const isLoading = loading.tours;
  const tableError = error.tours || error.deleting;

  return {
    tours: filteredTours,
    discords,
    rulesets,
    isLoading,
    error: tableError,
    handleEdit,
    handleDelete,
    clearError,
  };
};

// Hook for tour filters
export const useTourFilters = () => {
  const { filters, setFilters, clearFilters, discords, rulesets } = useTours();

  const updateFilter = useCallback(
    (key: keyof TourFilters, value: any) => {
      setFilters({ [key]: value });
    },
    [setFilters]
  );

  const removeFilter = useCallback(
    (key: keyof TourFilters) => {
      const newFilters = { ...filters };
      delete newFilters[key];
      setFilters(newFilters);
    },
    [filters, setFilters]
  );

  return {
    filters,
    discords,
    rulesets,
    updateFilter,
    removeFilter,
    clearFilters,
  };
};
